import { useState } from "react";
import { Box, Breadcrumbs, Divider, Link, Typography } from "@mui/joy";
import { ChevronRight } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";

import { Button, Textarea, TextField } from "../../../../shared";
import newOrganizationResolver from "../../../../libs/form-validations/organization";
import { LogoUploadField } from "../LogoUploadField";

function Breadcrumb() {
    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 0, ml: 1, mr: 2 }}
        >
            <Breadcrumbs separator={<ChevronRight size={18} />}>
                <Link
                    component={RouterLink}
                    to="/admin"
                    level="title-lg"
                    sx={{
                        fontWeight: 500,
                        textDecoration: "none",
                        color: "inherit",
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    Admin
                </Link>
                <Typography level="body-md" sx={{ fontWeight: 500 }}>
                    Organization
                </Typography>
            </Breadcrumbs>
        </Box>
    );
}

/** Placeholder until logged-in org is loaded from API. */
const DEFAULT_VALUES = {
    Name: "Colorado Roofing Company",
    ShortName: "CRC",
    Description:
        "Regional roofing contractor serving Colorado homeowners and commercial properties.",
    Website: "https://www.example.com",
    LogoUrl: "",
    LogoFile: null,
    IsActive: true,
    ContractTemplateId: "standard",
};

/**
 * Organization settings with prefilled fields.
 * Logo stays view-first with its own Edit action.
 */
export default function UserOrganization() {
    const [logoEditing, setLogoEditing] = useState(false);
    const methods = useForm({
        resolver: newOrganizationResolver,
        defaultValues: DEFAULT_VALUES,
        mode: "onChange",
    });

    const onSubmit = methods.handleSubmit((_data) => {
        // API integration intentionally deferred.
        setLogoEditing(false);
    });

    return (
        <Box
            flex={1}
            display="flex"
            flexDirection="column"
            maxHeight="100%"
            sx={{ overflow: "hidden", minWidth: 0, minHeight: 0 }}
        >
            <Breadcrumb />
            <Divider sx={{ mt: 0, ml: 0 }} />

            <Box flex={1} sx={{ minWidth: 0, overflow: "auto" }}>
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1, maxWidth: 700 }}>
                    <Typography level="title-lg">Organization settings</Typography>
                    <Typography level="body-sm" sx={{ color: "text.secondary", mt: 0.5 }}>
                        Update your organization name, description, logo, and basic details.
                    </Typography>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 700 }}>
                    <FormProvider {...methods}>
                        <form onSubmit={onSubmit}>
                            <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
                                <LogoUploadField
                                    editing={logoEditing}
                                    onEdit={() => setLogoEditing(true)}
                                    onCancelEdit={() => setLogoEditing(false)}
                                />

                                <TextField
                                    name="Name"
                                    label="Organization Name"
                                    helperText="Display name for your organization"
                                    placeholder="e.g., Colorado Roofing Company"
                                />

                                <TextField
                                    name="ShortName"
                                    label="Short Name"
                                    helperText="Optional short name or abbreviation"
                                    placeholder="e.g., CRC"
                                />

                                <Textarea
                                    name="Description"
                                    label="Description"
                                    helperText="Optional summary of your organization"
                                    placeholder="e.g., Regional roofing contractor serving Colorado homeowners"
                                    minRows={3}
                                />

                                <TextField
                                    name="Website"
                                    label="Website"
                                    helperText="Optional organization website"
                                    placeholder="e.g., https://www.example.com"
                                />

                                <Box display="flex" gap={1.5} mt={1}>
                                    <Button type="submit" variant="solid" color="primary">
                                        Save changes
                                    </Button>
                                </Box>
                            </Box>
                        </form>
                    </FormProvider>
                </Box>
            </Box>
        </Box>
    );
}
